import { Button, Icon } from "@qw/ui";
import { Link, useLocation } from "react-router";
import { UNNAMED_SCOPE, useBookScope } from "@/book/use-book-scope";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { scopeSegment, useInboxScope } from "@/providers/inbox-scope";
import { useInboxInsured } from "@/providers/use-inbox-insured";
import { SEARCH_OPERATOR_COPY, type SearchOperator } from "@/search/search-operators";

/**
 * Where "all email" is. The scope is the route, so releasing it is a navigation rather than a piece
 * of state, and `search` rides along — that is what makes removal a widening rather than a reset,
 * since the committed `?term=` survives and re-runs against the whole mailbox. The chip and the
 * Backspace handler in `qwib-search.tsx` share this so the two ways out cannot land in different
 * places.
 *
 * Removing the segment rather than navigating to `rootPath` keeps whatever the scope was standing
 * in front of: `/inbox/:insuredId/files` widens to `/inbox/files`, which the route table redirects
 * to the root on its own. `scopeSegment` is what makes that one string removal work for an agent or
 * an agency, whose segments are two path parts rather than one.
 *
 * The scope comes back alongside its release because every caller needs both, and asking twice
 * would be two chances to disagree about whether there is a scope at all.
 */
export const useClearScopeTarget = () => {
  const { pathname, search } = useLocation();
  const scope = useInboxScope();

  if (!scope) {
    return null;
  }

  return { scope, to: { pathname: pathname.replace(`/${scopeSegment(scope)}`, ""), search } };
};

/**
 * Every scope narrowing the box, as tokens in the field, and nothing at all when none is — an empty
 * wrapper would still take a gap in the input's row. They get a box of their own rather than sitting
 * as siblings of the input, because the row's `gap-2` is spacing between the row's parts and too
 * much between two tokens of the same kind.
 *
 * **`shrink-0` is load-bearing.** The input is `w-full`, so its flex base alone is the whole row and
 * flex has to take that width back from somewhere. Every chip is a `Button`, which is `flex-none`
 * and cannot give any up — so a shrinkable box around them shrinks anyway and the chips just
 * overflow it, landing on top of the caret (measured: an 8px gap becomes a 4px overlap). Before
 * these chips shared a box the sole chip was a direct child of the row and got `flex-none` from the
 * `Button` itself; this restores that. The field is what gives, which it can: `min-w-0` on the input.
 * A long value is capped by `max-w-40` and truncates inside it rather than eating the caret.
 *
 * Order is the Backspace order read backwards, and `handleKeyDown` says why: the last chip is the
 * one against the caret, so it is the one an emptied box deletes first. The operators go last
 * because they are text the broker typed there.
 *
 * `RouteScopeChip` decides for itself whether to render because it is resolving a name either way;
 * the other chips have nothing of their own to fetch, so this owns that call for them.
 */
export const SearchScopeChips = ({
  onRemoveUnlinked,
  onRemoveSubject,
  onRemoveSender,
}: {
  onRemoveUnlinked: () => void;
  onRemoveSubject: () => void;
  onRemoveSender: () => void;
}) => {
  const clearScopeTarget = useClearScopeTarget();
  const { subjectSearch, senderSearch, unlinked } = useInboxQueryStates();

  if (!clearScopeTarget && !unlinked && !subjectSearch && !senderSearch) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center gap-1">
      <RouteScopeChip />
      {/* A shorter label than the offer that turns it on: in the field, next to an insured name and
          behind a close icon, "Unlinked" reads as the tag it is, while in an otherwise-empty
          dropdown it would have to introduce itself. */}
      {unlinked && <ScopeChip label="Unlinked" ariaLabel="Remove unlinked filter" onRemove={onRemoveUnlinked} />}
      {subjectSearch && <OperatorScopeChip operator="subject" value={subjectSearch} onRemove={onRemoveSubject} />}
      {senderSearch && <OperatorScopeChip operator="from" value={senderSearch} onRemove={onRemoveSender} />}
    </div>
  );
};

/**
 * One token in the field, for a scope that is a query param and so comes off with a write.
 *
 * Every one of these is bookmarkable — `?unlinked=true`, `?subject=renewal`, `?from=sarah` — and a
 * cold load of one used to be a silently narrowed inbox with only a checkbox two menus deep to say
 * so. That is what the chip is for, ahead of being a way to widen.
 *
 * Both of the constraints below are easy to get wrong and invisible when you do, which is the reason
 * this is one component and not one per scope: the chip sits *inside* the text field, so taking
 * focus would pull the caret out of the box mid-search; and it renders *before* the input, so a tab
 * stop here would sit in front of the box on every route the scope is on — Tab into the header
 * search would reach the token rather than the caret. The keyboard's way in is Backspace from an
 * emptied box, which `handleKeyDown` owns.
 */
const ScopeChip = ({ label, ariaLabel, onRemove }: { label: string; ariaLabel: string; onRemove: () => void }) => (
  <Button
    variant="secondary"
    size="xs"
    className="max-w-40"
    aria-label={ariaLabel}
    onClick={onRemove}
    onMouseDown={(event) => event.preventDefault()}
    tabIndex={-1}
  >
    <Icon icon="close" />
    <span className="truncate">{label}</span>
  </Button>
);

/**
 * A committed `subject:` or `from:`, as the token it replaced in the field.
 *
 * The operator was typed *in the box*, so showing it back here is the whole reason the term is split
 * on commit rather than sent whole: the broker sees which dimension narrowed the list, and has a ✕
 * for it. Without that, `subject:renewal` would vanish into a query string and the box would look
 * like an ordinary search over a list that is anything but.
 */
const OperatorScopeChip = ({
  operator,
  value,
  onRemove,
}: {
  operator: SearchOperator;
  value: string;
  onRemove: () => void;
}) => {
  const { label, dimension } = SEARCH_OPERATOR_COPY[operator];

  return (
    <ScopeChip label={`${label}: ${value}`} ariaLabel={`Remove ${dimension} filter: ${value}`} onRemove={onRemove} />
  );
};

/**
 * The box's route scope — an insured, an agent or an agency — and the one chip that is not a
 * `ScopeChip`: the scope lives in the route, so releasing it is a navigation and the control has to
 * be a link.
 *
 * One component for all three kinds rather than one each, because they are the same chip: the same
 * position in the row, the same place in the Backspace order, the same removal. Only the name
 * differs, and `scope.kind` is already the word for what the scope narrows to, so the `aria-label`
 * needs nothing of its own.
 *
 * **The name never gates the chip.** The scope comes from the route and is already narrowing the
 * list, so a chip that waited for a name would leave the box quietly filtered with no way out —
 * which for a set scope is not a loading window but a standing possibility: the name is read off the
 * broker's own book, so an agent or agency with no open account there has none to read. See
 * `useBookScope`.
 */
const RouteScopeChip = () => {
  const clearScopeTarget = useClearScopeTarget();
  const { insured } = useInboxInsured();
  const bookScope = useBookScope();

  if (!clearScopeTarget) {
    return null;
  }

  const { scope, to } = clearScopeTarget;
  const name =
    scope.kind === "insured" ? insured?.name || "Unknown Insured" : (bookScope?.name ?? UNNAMED_SCOPE[scope.kind]);

  return (
    <Button variant="secondary" size="xs" className="max-w-40" asChild>
      <Link
        to={to}
        aria-label={`Remove ${scope.kind} filter: ${name}`}
        // `ScopeChip` says why both of these matter; they apply to an anchor the same way.
        onMouseDown={(event) => event.preventDefault()}
        tabIndex={-1}
      >
        <Icon icon="close" />
        <span className="truncate">{name}</span>
      </Link>
    </Button>
  );
};
